import {
  buildDirectoryTree,
  type SharedFolderDirectoryTreeNode,
} from '../crypto/sharedFolderManifest';
import { applyI18n } from '../i18n';
import { fileTypeImg } from './fileTypeImg';
import { applyDirTreeNodeContent, type FilesTreeSearchState } from './filesTreeSearch';

export interface FilesDirectoryTreeOptions {
  currentPath: string;
  expandedPaths: Set<string>;
  ariaLabelKey: string;
  /** When set, the tree is re-rooted so this path appears as `/` (guest subfolder scope). */
  rootPath?: string;
  /** When set, applies hit badges and gray styling for cross-folder search. */
  search?: FilesTreeSearchState | null;
  onSelect(path: string): void;
  onToggle(path: string, expanded: boolean): void;
}

export function renderFilesDirectoryTree(
  host: HTMLElement,
  manifest: Parameters<typeof buildDirectoryTree>[0],
  options: FilesDirectoryTreeOptions,
): void {
  const tree = scopeDirectoryTree(buildDirectoryTree(manifest), options.rootPath ?? '');
  host.className = 'files-dir-tree';
  host.setAttribute('role', 'tree');
  host.setAttribute('tabindex', '0');
  host.setAttribute('data-i18n-aria-label', options.ariaLabelKey);
  host.replaceChildren();

  // Root is always expanded so the tree has a visible entry point.
  options.expandedPaths.add(options.rootPath ?? '');

  const list = document.createElement('ul');
  list.className = 'files-dir-tree__list';
  list.append(renderNode(tree, 1, options));
  host.append(list);
  applyI18n(host);

  host.onkeydown = (event: KeyboardEvent) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(event.key)) {
      return;
    }

    const items = Array.from(host.querySelectorAll<HTMLElement>('.files-dir-tree__node'));
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const currentIndex = active ? items.indexOf(active) : -1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[Math.min(items.length - 1, currentIndex + 1)]?.focus();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      items[Math.max(0, currentIndex - 1)]?.focus();
      return;
    }

    const current = active?.closest<HTMLElement>('[data-tree-path]');
    if (!current) {
      items[0]?.focus();
      return;
    }

    const path = current.dataset.treePath ?? '';
    const hasChildren = current.dataset.hasChildren === 'true';
    const expanded = current.dataset.expanded === 'true';
    if (event.key === 'ArrowRight' && hasChildren && !expanded) {
      event.preventDefault();
      options.onToggle(path, true);
      return;
    }
    if (event.key === 'ArrowLeft' && hasChildren && expanded) {
      event.preventDefault();
      options.onToggle(path, false);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (path === options.currentPath && hasChildren) {
        options.onToggle(path, !expanded);
      } else {
        options.onSelect(path);
      }
    }
  };
}

/**
 * Expand ancestors of `path` so the selection is visible, but do not expand `path` itself
 * (so the selected folder can be collapsed via chevron or re-click).
 */
export function ensureExpandedAncestors(
  path: string,
  expandedPaths: Set<string>,
  rootPath = '',
): void {
  expandedPaths.add(rootPath);
  let current = path;
  while (current) {
    if (rootPath && current === rootPath) {
      break;
    }
    const slashIndex = current.lastIndexOf('/');
    current = slashIndex === -1 ? '' : current.slice(0, slashIndex);
    if (!current && !rootPath) {
      expandedPaths.add('');
      break;
    }
    if (current) {
      expandedPaths.add(current);
    }
    if (rootPath && current === rootPath) {
      break;
    }
  }
}

function renderNode(
  node: SharedFolderDirectoryTreeNode,
  level: number,
  options: FilesDirectoryTreeOptions,
): HTMLLIElement {
  const item = document.createElement('li');
  item.className = 'files-dir-tree__item';

  const row = document.createElement('div');
  row.className = 'files-dir-tree__row';
  row.dataset.treePath = node.path;

  const hasChildren = node.children.length > 0;
  const expanded = hasChildren && options.expandedPaths.has(node.path);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'files-dir-tree__toggle';
  toggle.textContent = hasChildren ? (expanded ? '▾' : '▸') : '';
  toggle.disabled = !hasChildren;
  toggle.setAttribute('aria-hidden', hasChildren ? 'false' : 'true');
  toggle.addEventListener('click', () => {
    if (!hasChildren) {
      return;
    }
    options.onToggle(node.path, !expanded);
  });

  const button = document.createElement('button');
  button.type = 'button';
  button.className = `files-dir-tree__node${node.path === options.currentPath ? ' files-dir-tree__node--active' : ''}`;
  button.style.paddingLeft = `${Math.max(0, level - 1) * 1}rem`;
  applyDirTreeNodeContent(button, {
    icon: fileTypeImg('folder', 16),
    name: node.name,
    folderKey: node.path,
    search: options.search,
  });
  button.dataset.treePath = node.path;
  button.dataset.expanded = String(expanded);
  button.dataset.hasChildren = String(hasChildren);
  button.setAttribute('role', 'treeitem');
  button.setAttribute('aria-level', String(level));
  button.setAttribute('aria-selected', String(node.path === options.currentPath));
  if (hasChildren) {
    button.setAttribute('aria-expanded', String(expanded));
  }
  button.addEventListener('click', () => {
    if (node.path === options.currentPath && hasChildren) {
      options.onToggle(node.path, !expanded);
      return;
    }
    options.onSelect(node.path);
  });

  row.append(toggle, button);
  item.append(row);

  if (hasChildren && expanded) {
    const list = document.createElement('ul');
    list.className = 'files-dir-tree__list';
    for (const child of node.children) {
      list.append(renderNode(child, level + 1, options));
    }
    item.append(list);
  }

  return item;
}

function scopeDirectoryTree(
  tree: SharedFolderDirectoryTreeNode,
  rootPath: string,
): SharedFolderDirectoryTreeNode {
  if (!rootPath) {
    return tree;
  }

  const target = findTreeNode(tree, rootPath);
  return {
    path: rootPath,
    name: '/',
    entryId: target?.entryId ?? 'guest-root',
    createdAt: target?.createdAt ?? '',
    createdBy: target?.createdBy,
    children: target?.children ?? [],
  };
}

function findTreeNode(
  node: SharedFolderDirectoryTreeNode,
  path: string,
): SharedFolderDirectoryTreeNode | null {
  if (node.path === path) {
    return node;
  }

  for (const child of node.children) {
    const match = findTreeNode(child, path);
    if (match) {
      return match;
    }
  }

  return null;
}
